/* Desktop slide-out drawer — single shared drawer with tabs */
(function () {
  'use strict';

  class HeaderSlideDrawer extends HTMLElement {
    constructor() {
      super();
      this.triggers = Array.from(this.querySelectorAll('[data-slide-drawer-open]'));
      this.overlay = this.querySelector('[data-slide-drawer-overlay]');
      this.panel = this.overlay ? this.overlay.querySelector('[data-slide-drawer-panel-wrap]') : null;
      this.lastFocused = null;
      this.closeToken = 0;
      this.closeTimer = null;
      this.openTimer = null;
      // Hover-to-open only on desktop pointers that can actually hover.
      this.hoverMQ = window.matchMedia('(min-width: 990px) and (hover: hover)');
      // Hover-intent: only open if the pointer rests on the trigger this long,
      // so brushing past (e.g. on the way to New In) doesn't trigger it.
      this.OPEN_DELAY = 450;
      this.CLOSE_DELAY = 200;

      this._onTriggerClick = this.onTriggerClick.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onKey = this.onKey.bind(this);
      this._onTriggerEnter = this.onTriggerEnter.bind(this);
      this._onTriggerLeave = this.onTriggerLeave.bind(this);
      this._scheduleClose = this.scheduleClose.bind(this);
      this._cancelClose = this.cancelClose.bind(this);
    }

    connectedCallback() {
      this.triggers.forEach((btn) => {
        btn.addEventListener('click', this._onTriggerClick);
        // Open (after hover-intent) / switch tabs on hover (desktop).
        btn.addEventListener('mouseenter', this._onTriggerEnter);
        btn.addEventListener('mouseleave', this._onTriggerLeave);
      });
      // Keep it open while the pointer is over the panel; close shortly after
      // leaving. The delay bridges the gap between the trigger and the panel.
      if (this.panel) {
        this.panel.addEventListener('mouseenter', this._cancelClose);
        this.panel.addEventListener('mouseleave', this._scheduleClose);
      }
      this.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
    }

    onTriggerEnter(e) {
      if (!this.hoverMQ.matches || !this.overlay) return;
      this.cancelClose();
      const btn = e.currentTarget;
      const tab = btn.getAttribute('data-target-tab');
      // Already open: switch tabs instantly (no intent delay).
      if (this.overlay.classList.contains('is-open')) {
        this.cancelOpen();
        this.switchTab(tab);
        return;
      }
      // Closed: wait for hover-intent before sliding in.
      this.cancelOpen();
      this.openTimer = setTimeout(() => {
        this.switchTab(tab, { silent: true });
        this.open(btn, { hover: true });
      }, this.OPEN_DELAY);
    }

    onTriggerLeave() {
      if (!this.hoverMQ.matches) return;
      // Abort a pending open (brushed past) and close if already open.
      this.cancelOpen();
      this.scheduleClose();
    }

    scheduleClose() {
      if (!this.hoverMQ.matches) return;
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        if (this.overlay && this.overlay.classList.contains('is-open')) this.close();
      }, this.CLOSE_DELAY);
    }

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    cancelOpen() {
      if (this.openTimer) {
        clearTimeout(this.openTimer);
        this.openTimer = null;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
    }

    onTriggerClick(e) {
      if (!this.overlay) return;
      const btn = e.currentTarget;
      const tab = btn.getAttribute('data-target-tab');
      e.preventDefault();
      const isOpen = this.overlay.classList.contains('is-open');
      const currentTab = this.overlay.getAttribute('data-active-tab') || '';
      if (isOpen && currentTab === tab) {
        this.close();
      } else if (isOpen) {
        this.switchTab(tab);
      } else {
        this.switchTab(tab, { silent: true });
        this.open(btn);
      }
    }

    onDocClick(e) {
      if (e.target.closest('[data-slide-drawer-close]') || e.target.closest('[data-slide-drawer-backdrop]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const tabBtn = e.target.closest('[data-slide-drawer-tab]');
      if (tabBtn && this.overlay && this.overlay.contains(tabBtn)) {
        e.preventDefault();
        const tab = tabBtn.getAttribute('data-tab-target') || '';
        this.switchTab(tab);
        return;
      }
      const reveal = e.target.closest('[data-slide-drawer-reveal]');
      if (reveal && this.overlay && this.overlay.contains(reveal)) {
        e.preventDefault();
        this.revealSub(reveal);
        return;
      }
      const link = e.target.closest('a.slide-drawer__row, a.slide-drawer__top-link');
      if (link && window.reasnAnalytics) {
        const inDrawer = !!link.closest('[data-slide-drawer-overlay]');
        const event = inDrawer ? 'drawer_link_clicked' : 'nav_link_clicked';
        const props = {
          surface: 'desktop',
          label: (link.textContent || '').trim(),
          href: link.getAttribute('href') || ''
        };
        if (inDrawer && this.overlay) {
          props.tab = this.overlay.getAttribute('data-active-tab') || '';
          props.depth = link.classList.contains('slide-drawer__row--sub') ? 3 : 2;
        }
        window.reasnAnalytics.track(event, props);
      }
    }

    onKey(e) {
      if (e.key === 'Escape' && this.overlay && this.overlay.classList.contains('is-open')) {
        e.preventDefault();
        this.close();
      }
    }

    open(trigger, opts) {
      if (!this.overlay) return;
      const viaHover = opts && opts.hover;
      this.openedViaHover = viaHover;
      this.closeToken++;
      this.lastFocused = trigger || document.activeElement;
      this.overlay.hidden = false;
      void this.overlay.offsetWidth;
      this.overlay.classList.add('is-open');
      // is-hover keeps the trigger above the overlay and the backdrop
      // click-through, so hovering the trigger doesn't get stolen (no bounce).
      this.overlay.classList.toggle('is-hover', !!viaHover);
      this.syncTriggerStates();
      // Don't scroll-lock on hover: removing the scrollbar shifts the header,
      // which moves the trigger out from under the cursor and makes the drawer
      // bounce open/closed. Hover behaves like a mega-menu (page still scrolls).
      if (!viaHover) {
        document.documentElement.style.overflow = 'hidden';
        const focusTarget = this.overlay.querySelector('[data-slide-drawer-close]');
        if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 50);
      }
      if (window.reasnAnalytics) {
        window.reasnAnalytics.track('drawer_opened', {
          surface: 'desktop',
          tab: this.overlay.getAttribute('data-active-tab') || ''
        });
      }
    }

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-open');
      this.overlay.classList.remove('is-hover');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';

      const myToken = ++this.closeToken;
      const finish = () => {
        if (myToken !== this.closeToken) return;
        if (this.overlay.classList.contains('is-open')) return;
        this.resetSubs();
        this.overlay.hidden = true;
      };

      if (this.panel) {
        const onEnd = (ev) => {
          if (ev && ev.target !== this.panel) return;
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        };
        this.panel.addEventListener('transitionend', onEnd);
        setTimeout(() => {
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        }, 600);
      } else {
        finish();
      }

      // Restore focus to the trigger only for keyboard/click opens. On hover,
      // programmatically focusing it triggers :focus-visible and draws the box.
      if (!this.openedViaHover && this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    resetSubs() {
      if (!this.overlay) return;
      this.overlay.classList.remove('has-sub');
      this.overlay.querySelectorAll('[data-slide-drawer-sub]').forEach((s) => {
        s.hidden = true;
      });
      this.overlay.querySelectorAll('[data-slide-drawer-reveal]').forEach((r) => {
        r.classList.remove('is-active');
        r.setAttribute('aria-expanded', 'false');
      });
    }

    revealSub(button) {
      if (!this.overlay) return;
      const subId = button.getAttribute('data-target-sub');
      if (window.reasnAnalytics) {
        window.reasnAnalytics.track('drawer_sub_revealed', {
          surface: 'desktop',
          tab: this.overlay.getAttribute('data-active-tab') || '',
          parent: (button.textContent || '').trim()
        });
      }
      this.overlay.querySelectorAll('[data-slide-drawer-sub]').forEach((s) => {
        s.hidden = s.dataset.subId !== subId;
      });
      this.overlay.querySelectorAll('[data-slide-drawer-reveal]').forEach((r) => {
        const isThis = r === button;
        r.classList.toggle('is-active', isThis);
        r.setAttribute('aria-expanded', isThis ? 'true' : 'false');
      });
      this.overlay.classList.add('has-sub');
    }

    switchTab(tab, opts) {
      if (!this.overlay || !tab) return;
      const previous = this.overlay.getAttribute('data-active-tab') || '';
      this.overlay.setAttribute('data-active-tab', tab);
      if (!opts || !opts.silent) {
        if (window.reasnAnalytics && previous !== tab) {
          window.reasnAnalytics.track('drawer_tab_switched', {
            surface: 'desktop',
            from: previous,
            to: tab
          });
        }
      }
      this.overlay.querySelectorAll('[data-slide-drawer-tab]').forEach((btn) => {
        const isThis = btn.getAttribute('data-tab-target') === tab;
        btn.classList.toggle('is-active', isThis);
        btn.setAttribute('aria-selected', isThis ? 'true' : 'false');
      });
      this.overlay.querySelectorAll('[data-slide-drawer-pane]').forEach((pane) => {
        pane.hidden = pane.getAttribute('data-pane-tab') !== tab;
      });
      this.resetSubs();
      this.syncTriggerStates();
    }

    syncTriggerStates() {
      if (!this.overlay) return;
      const isOpen = this.overlay.classList.contains('is-open');
      const activeTab = this.overlay.getAttribute('data-active-tab') || '';
      this.triggers.forEach((t) => {
        const isThis = t.getAttribute('data-target-tab') === activeTab;
        t.setAttribute('aria-expanded', isOpen && isThis ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('header-slide-drawer')) {
    customElements.define('header-slide-drawer', HeaderSlideDrawer);
  }
})();
